import React, { useEffect, useRef, useState } from 'react'
import {
  FiArrowRight,
  FiAward,
  FiBookOpen,
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiCpu,
  FiHeart,
  FiLayers,
  FiMinus,
  FiStar,
  FiTarget,
} from 'react-icons/fi'
import FinalCtaPanel from '../FinalCtaPanel'
import { APP_ENTRY_HREF } from '../../constants/navigation'
import './about.css'
import featureReactivation from '../../assets/home/feature-reactivation-hd.webp'

const heroMemoryFragments = [
  { text: 'Recuerdo', lang: 'es', code: 'ES', className: 'hola' },
  { text: '我记得', lang: 'zh-Hans', code: 'ZH', className: 'nihao' },
  { text: 'Je me souviens', lang: 'fr', code: 'FR', className: 'bonjour' },
  { text: 'Ricordo', lang: 'it', code: 'IT', className: 'ciao' },
  { text: '覚えている', lang: 'ja', code: 'JA', className: 'konnichiwa' },
  { text: 'أتذكّر', lang: 'ar', code: 'AR', className: 'marhaba', dir: 'rtl' },
  { text: 'Eu me lembro', lang: 'pt-BR', code: 'PT', className: 'ola' },
  { text: 'I remember', lang: 'en', code: 'EN', className: 'hello' },
]

const heroLanguageCurrents = {
  desktop: [
    'M420 80 C500 84 564 112 625 145',
    'M550 100 C578 112 602 128 625 145',
    'M745 60 C710 84 666 118 625 145',
    'M960 140 C842 136 724 139 625 145',
    'M470 175 C524 169 578 157 625 145',
    'M940 225 C815 205 715 175 625 145',
    'M540 255 C570 220 601 180 625 145',
    'M690 190 C667 180 645 161 625 145',
  ],
  mobile: [
    'M80 155 C298 160 500 176 640 195',
    'M880 125 C786 145 704 174 640 195',
    'M605 135 C610 150 624 180 640 195',
    'M920 240 C806 231 712 211 640 195',
  ],
}

const lifecycleNeuralLinks = [
  { d: 'M62 96 C78 84 91 72 112 52', role: 'encoding' },
  { d: 'M82 78 C101 84 119 74 142 68', role: 'encoding' },
  { d: 'M82 78 C83 92 87 104 94 112', role: 'support' },
  { d: 'M94 112 C116 104 135 111 159 102', role: 'core' },
  { d: 'M130 132 C144 125 153 115 159 102', role: 'core' },
  { d: 'M159 102 C168 84 178 70 188 60', role: 'retrieval' },
  { d: 'M188 60 C201 66 211 72 218 78', role: 'retrieval' },
  { d: 'M218 78 C233 87 241 94 258 96', role: 'retrieval' },
  { d: 'M159 102 C181 106 207 108 232 112', role: 'retrieval' },
  { d: 'M159 102 C174 118 185 127 196 132', role: 'support' },
  { d: 'M196 132 C183 139 171 143 160 145', role: 'support' },
  { d: 'M130 132 C141 140 151 144 160 145', role: 'support' },
]

const lifecycleNeuralNodes = [
  { cx: 62, cy: 96, role: 'temporal' },
  { cx: 82, cy: 78, role: 'temporal' },
  { cx: 112, cy: 52, role: 'frontal' },
  { cx: 142, cy: 68, role: 'frontal' },
  { cx: 94, cy: 112, role: 'temporal' },
  { cx: 130, cy: 132, role: 'hippocampal' },
  { cx: 159, cy: 102, role: 'engram' },
  { cx: 188, cy: 60, role: 'frontal' },
  { cx: 218, cy: 78, role: 'frontal' },
  { cx: 258, cy: 96, role: 'temporal' },
  { cx: 232, cy: 112, role: 'temporal' },
  { cx: 196, cy: 132, role: 'hippocampal' },
  { cx: 160, cy: 145, role: 'hippocampal' },
]

const LifecycleNeuralMap = ({ state }) => (
  <svg
    className={`about-page__lifecycle-science about-page__lifecycle-science--${state}`}
    viewBox="0 0 320 190"
    preserveAspectRatio="xMidYMid meet"
    focusable="false"
    aria-hidden="true"
    data-lifecycle-science={state}
  >
    <path
      className="about-page__science-brain"
      d="M160 25 C145 12 121 12 105 24 C81 18 58 35 55 59 C31 69 26 96 41 114 C32 137 50 158 75 157 C92 176 121 176 141 160 C148 168 154 172 160 174 C166 172 172 168 179 160 C199 176 228 176 245 157 C270 158 288 137 279 114 C294 96 289 69 265 59 C262 35 239 18 215 24 C199 12 175 12 160 25 Z"
    />
    <path
      className="about-page__science-midline"
      d="M160 25 C151 51 168 70 157 91 C149 111 168 133 160 174"
    />
    <g className="about-page__science-folds">
      <path d="M64 68 C82 58 91 59 105 68 C118 77 132 72 142 58" />
      <path d="M52 111 C72 102 84 100 97 112 C108 122 122 118 135 107" />
      <path d="M80 145 C98 134 113 138 125 151" />
      <path d="M256 68 C238 58 229 59 215 68 C202 77 188 72 178 58" />
      <path d="M268 111 C248 102 236 100 223 112 C212 122 198 118 185 107" />
      <path d="M240 145 C222 134 207 138 195 151" />
    </g>

    <path
      className="about-page__science-hippocampus"
      d="M105 119 C116 143 145 143 159 121 C174 143 204 143 215 119"
    />

    <g className="about-page__science-links">
      {lifecycleNeuralLinks.map(({ d, role }, index) => (
        <path
          className={`about-page__science-link about-page__science-link--${role}`}
          d={d}
          pathLength="1"
          style={{ '--science-delay': `${180 + index * 72}ms` }}
          key={d}
        />
      ))}
    </g>

    <g className="about-page__science-nodes">
      {lifecycleNeuralNodes.map(({ cx, cy, role }, index) => (
        <circle
          className={`about-page__science-node about-page__science-node--${role}`}
          cx={cx}
          cy={cy}
          r={role === 'engram' ? 5 : 3.2}
          style={{ '--science-delay': `${360 + index * 86}ms` }}
          key={`${cx}-${cy}`}
        />
      ))}
    </g>

    {state === 'acquisition' && (
      <g className="about-page__science-cue about-page__science-cue--acquisition">
        <path d="M8 96 C16 76 24 116 32 96 S48 76 58 96" pathLength="1" />
        <path
          className="about-page__science-route"
          d="M8 96 C31 96 44 96 62 96 C90 94 116 78 142 68 C152 76 157 88 159 102"
          pathLength="1"
        />
        <circle cx="8" cy="96" r="3.2" />
      </g>
    )}

    {state === 'attrition' && (
      <g className="about-page__science-cue about-page__science-cue--attrition">
        <path d="M8 96 C16 83 24 109 32 96 S48 83 58 96" pathLength="1" />
        <path
          className="about-page__science-route about-page__science-route--attenuated"
          d="M8 96 C31 96 44 96 62 96 C90 94 116 78 142 68 C152 76 157 88 159 102"
          pathLength="1"
        />
        <circle cx="8" cy="96" r="2.6" />
      </g>
    )}

    {state === 'reactivation' && (
      <g className="about-page__science-reactivation">
        <path
          className="about-page__science-route about-page__science-route--reactivation"
          d="M8 112 C30 112 45 101 62 96 C88 98 111 116 130 132 C144 126 153 114 159 102 C181 105 207 108 232 112 C244 107 252 101 270 98"
          pathLength="1"
        />
        <circle className="about-page__science-activation-ring" cx="159" cy="102" r="10" />
        <g className="about-page__science-spark">
          <path d="M159 83 L159 91" />
          <path d="M159 113 L159 121" />
          <path d="M140 102 L148 102" />
          <path d="M170 102 L178 102" />
          <path d="M146 89 L152 95" />
          <path d="M166 109 L172 115" />
        </g>
      </g>
    )}
  </svg>
)

const lifecycleSteps = [
  {
    id: 'acquisition',
    number: '01',
    tone: 'learning',
    titleFr: 'Acquisition',
    titleEn: 'Learning',
    statementFr: 'Chaque son crée une connexion.',
    statementEn: 'Every sound forms a connection.',
    textFr: 'Vous découvrez une langue, vous apprenez, vous progressez.',
    textEn: 'You discover a language, you learn, you progress.',
  },
  {
    id: 'attrition',
    number: '02',
    tone: 'forgetting',
    titleFr: 'Attrition',
    titleEn: 'Forgetting',
    statementFr: 'Le signal faiblit, sans disparaître.',
    statementEn: 'The signal fades. It never vanishes.',
    textFr: 'La vie passe, la pratique diminue, la langue s’estompe.',
    textEn: 'Life moves on, practice fades, the language fades.',
  },
  {
    id: 'reactivation',
    number: '03',
    tone: 'reactivation',
    titleFr: 'Réactivation',
    titleEn: 'Reactivation',
    statementFr: 'Le bon contexte rallume la connexion.',
    statementEn: 'The right context restores the connection.',
    textFr: 'Avec le bon contexte, la langue revient naturellement.',
    textEn: 'With the right context, the language comes back naturally.',
  },
]

const reactivationSignals = [
  {
    id: 'context',
    number: '01',
    icon: FiCalendar,
    titleFr: 'Contexte vivant',
    titleEn: 'Living context',
    proofFr: 'Des scènes immersives ancrées dans la vie réelle.',
    proofEn: 'Immersive scenes rooted in real life.',
    stateFr: 'Le contexte redonne du sens.',
    stateEn: 'Context restores meaning.',
  },
  {
    id: 'memory',
    number: '02',
    icon: FiCpu,
    titleFr: 'Mémoire ciblée',
    titleEn: 'Targeted memory',
    proofFr: 'Des déclencheurs conçus à partir des sciences cognitives.',
    proofEn: 'Memory cues shaped by cognitive science.',
    stateFr: 'Le bon signal retrouve le bon chemin.',
    stateEn: 'The right cue finds the right pathway.',
  },
  {
    id: 'emotion',
    number: '03',
    icon: FiHeart,
    titleFr: 'Réengagement émotionnel',
    titleEn: 'Emotional reconnection',
    proofFr: 'L’implication et l’émotion rendent les mots à nouveau vivants.',
    proofEn: 'Emotion and involvement bring words back to life.',
    stateFr: 'L’émotion rallume la connexion.',
    stateEn: 'Emotion reignites the connection.',
  },
]

const reactivationBranches = [
  'M 800 838 C 780 728 824 640 800 520 C 780 410 825 282 800 104',
  'M 800 600 C 722 520 650 458 530 395 C 460 358 418 306 386 244',
  'M 800 596 C 884 514 958 460 1080 392 C 1146 354 1190 306 1222 240',
  'M 800 474 C 724 405 680 336 634 240 C 610 190 570 154 522 126',
  'M 800 468 C 878 398 918 328 970 232 C 998 180 1038 145 1088 118',
  'M 800 350 C 758 292 736 230 724 154',
  'M 800 346 C 842 286 866 224 878 146',
]

const reactivationRoutes = {
  context: 'M 800 838 C 780 728 824 640 800 520 C 724 405 680 336 634 240 C 610 190 570 154 522 126',
  memory: 'M 800 838 C 780 728 824 640 800 520 C 780 410 825 282 800 104',
  emotion: 'M 800 838 C 780 728 824 640 800 520 C 878 398 918 328 970 232 C 998 180 1038 145 1088 118',
}

const reactivationDestinations = {
  context: [522, 126],
  memory: [800, 104],
  emotion: [1088, 118],
}

const reactivationNodes = [
  [386, 244],
  [522, 126],
  [634, 240],
  [724, 154],
  [800, 104],
  [878, 146],
  [970, 232],
  [1088, 118],
  [1222, 240],
]

const reactivationMotes = [
  ['13%', '26%', '5px', '-1.2s'],
  ['24%', '70%', '3px', '-4.8s'],
  ['36%', '18%', '4px', '-2.6s'],
  ['44%', '58%', '3px', '-6.1s'],
  ['54%', '13%', '4px', '-3.4s'],
  ['63%', '72%', '5px', '-5.2s'],
  ['72%', '31%', '3px', '-0.8s'],
  ['84%', '58%', '4px', '-4.1s'],
]

const ReactivationMemoryTree = ({ activeSignal, pulseKey }) => (
  <div
    className="about-page__memory-tree"
    aria-hidden="true"
    data-reactivation-motion="memory-tree"
  >
    <span className="about-page__memory-tree-media">
      <img
        src={featureReactivation}
        alt=""
        width="1600"
        height="900"
        loading="lazy"
        decoding="async"
      />
    </span>

    <svg viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice" focusable="false">
      <g className="about-page__memory-branches">
        {reactivationBranches.map((path, index) => (
          <path
            d={path}
            pathLength="1"
            style={{ '--branch-index': index }}
            key={path}
          />
        ))}
      </g>
      <path
        className="about-page__memory-current"
        d={reactivationRoutes[activeSignal]}
        pathLength="1"
        key={`${activeSignal}-${pulseKey}`}
      />
      <g
        className="about-page__memory-destination"
        key={`destination-${activeSignal}-${pulseKey}`}
      >
        <circle
          cx={reactivationDestinations[activeSignal][0]}
          cy={reactivationDestinations[activeSignal][1]}
          r="16"
        />
        <circle
          cx={reactivationDestinations[activeSignal][0]}
          cy={reactivationDestinations[activeSignal][1]}
          r="5"
        />
      </g>
      <g className="about-page__memory-nodes">
        {reactivationNodes.map(([cx, cy], index) => (
          <circle
            cx={cx}
            cy={cy}
            r={index === 4 ? 12 : index % 3 === 0 ? 9 : 6}
            style={{ '--node-index': index }}
            key={`${cx}-${cy}`}
          />
        ))}
      </g>
      <g className="about-page__memory-core">
        <circle cx="800" cy="520" r="24" />
        <circle cx="800" cy="520" r="42" />
      </g>
    </svg>

    <span className="about-page__memory-aura" />
    <span className="about-page__memory-motes">
      {reactivationMotes.map(([left, top, size, delay]) => (
        <i style={{ '--mote-left': left, '--mote-top': top, '--mote-size': size, '--mote-delay': delay }} key={`${left}-${top}`} />
      ))}
    </span>
    <span className="about-page__memory-status">
      <i />
      <span>Synapse active</span>
      <small>Memory online</small>
    </span>
  </div>
)

const comparisonRows = [
  {
    icon: FiTarget,
    featureFr: 'Objectif principal',
    featureEn: 'Main goal',
    classicFr: 'Apprendre de zéro',
    classicEn: 'Learn from zero',
    octoFr: 'Réactiver ce que vous savez déjà',
    octoEn: 'Reactivate what you already know',
  },
  {
    icon: FiLayers,
    featureFr: 'Approche',
    featureEn: 'Approach',
    classicFr: 'Leçons, vocabulaire, règles',
    classicEn: 'Lessons, vocabulary, rules',
    octoFr: 'Scènes immersives, mémoire, émotion',
    octoEn: 'Immersive scenes, memory, emotion',
  },
  {
    icon: FiBookOpen,
    featureFr: 'Mémoire',
    featureEn: 'Memory',
    classicFr: 'Mémorisation passive',
    classicEn: 'Passive memorization',
    octoFr: 'Réactivation active et contextuelle',
    octoEn: 'Active, contextual reactivation',
  },
  {
    icon: FiStar,
    featureFr: 'Motivation',
    featureEn: 'Motivation',
    classicFr: 'Streaks, points, rappels',
    classicEn: 'Streaks, points, reminders',
    octoFr: 'Immersion, sens, connexion personnelle',
    octoEn: 'Immersion, meaning, personal connection',
  },
  {
    icon: FiAward,
    featureFr: 'Résultat',
    featureEn: 'Outcome',
    classicFr: 'Nouvelles connaissances',
    classicEn: 'New knowledge',
    octoFr: 'Langue retrouvée, confiance retrouvée',
    octoEn: 'Language regained, confidence regained',
  },
]

const comparisonNetworkPaths = [
  'M44 114 C190 78 286 146 430 138 C620 127 720 68 1154 94',
  'M26 242 C176 218 280 260 442 252 C646 241 806 172 1176 190',
  'M38 372 C212 340 294 392 470 378 C684 362 810 300 1164 318',
  'M20 508 C172 476 300 534 458 514 C654 490 824 432 1182 448',
  'M170 58 C238 152 292 220 430 252 C592 289 724 240 876 146',
  'M234 574 C326 476 390 432 512 414 C678 390 754 444 954 522',
]

const comparisonNetworkNodes = [
  [44, 114, 3.2],
  [286, 146, 2.4],
  [430, 138, 4.2],
  [720, 68, 2.6],
  [1154, 94, 3.8],
  [26, 242, 2.8],
  [442, 252, 4.4],
  [806, 172, 2.6],
  [1176, 190, 3.6],
  [38, 372, 2.8],
  [470, 378, 4.4],
  [810, 300, 2.6],
  [1164, 318, 3.8],
  [20, 508, 2.6],
  [458, 514, 4.2],
  [824, 432, 2.8],
  [1182, 448, 3.6],
]

const ComparisonNetwork = () => (
  <div
    className="about-page__comparison-ambient"
    aria-hidden="true"
    data-comparison-motion="synapse-network"
  >
    <span className="about-page__comparison-glow about-page__comparison-glow--gold" />
    <span className="about-page__comparison-glow about-page__comparison-glow--blue" />
    <svg
      className="about-page__comparison-network"
      viewBox="0 0 1200 620"
      preserveAspectRatio="none"
      focusable="false"
    >
      <g className="about-page__comparison-network-lines">
        {comparisonNetworkPaths.map((path, index) => (
          <path
            d={path}
            pathLength="1"
            style={{ '--comparison-network-index': index }}
            key={path}
          />
        ))}
      </g>
      <g className="about-page__comparison-network-nodes">
        {comparisonNetworkNodes.map(([cx, cy, radius], index) => (
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            style={{ '--comparison-network-index': index }}
            key={`${cx}-${cy}`}
          />
        ))}
      </g>
    </svg>
  </div>
)

const About = () => {
  const [isMissionOpen, setIsMissionOpen] = useState(false)
  const [isLifecycleVisible, setIsLifecycleVisible] = useState(false)
  const [visibleLifecycleSteps, setVisibleLifecycleSteps] = useState([])
  const [isReactivationVisible, setIsReactivationVisible] = useState(false)
  const [isReactivationInView, setIsReactivationInView] = useState(false)
  const [isComparisonVisible, setIsComparisonVisible] = useState(false)
  const [isComparisonInView, setIsComparisonInView] = useState(false)
  const [activeReactivationSignal, setActiveReactivationSignal] = useState('context')
  const [reactivationPulseKey, setReactivationPulseKey] = useState(0)
  const lifecycleSectionRef = useRef(null)
  const reactivationSectionRef = useRef(null)
  const comparisonSectionRef = useRef(null)

  useEffect(() => {
    const section = lifecycleSectionRef.current
    if (!section) return undefined

    const chapters = Array.from(section.querySelectorAll('[data-lifecycle-chapter]'))
    const revealAll = () => {
      setIsLifecycleVisible(true)
      setVisibleLifecycleSteps(lifecycleSteps.map((step) => step.id))
    }

    if (typeof window.IntersectionObserver !== 'function') {
      revealAll()
      return undefined
    }

    const sectionObserver = new window.IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return
      setIsLifecycleVisible(true)
      sectionObserver.disconnect()
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' })

    const chapterObserver = new window.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        const chapterId = entry.target.getAttribute('data-lifecycle-chapter')
        setVisibleLifecycleSteps((visibleSteps) => (
          visibleSteps.includes(chapterId) ? visibleSteps : [...visibleSteps, chapterId]
        ))
        chapterObserver.unobserve(entry.target)
      })
    }, { threshold: 0.28, rootMargin: '0px 0px -8% 0px' })

    sectionObserver.observe(section)
    chapters.forEach((chapter) => chapterObserver.observe(chapter))

    return () => {
      sectionObserver.disconnect()
      chapterObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    const section = reactivationSectionRef.current
    if (!section) return undefined

    const revealSection = () => {
      setIsReactivationVisible(true)
      setIsReactivationInView(true)
    }

    if (typeof window.IntersectionObserver !== 'function') {
      revealSection()
      return undefined
    }

    const observer = new window.IntersectionObserver(([entry]) => {
      setIsReactivationInView(entry.isIntersecting)
      if (entry.isIntersecting) setIsReactivationVisible(true)
    }, { threshold: 0.05, rootMargin: '0px 0px -2% 0px' })

    observer.observe(section)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const section = comparisonSectionRef.current
    if (!section) return undefined

    const revealSection = () => {
      setIsComparisonVisible(true)
      setIsComparisonInView(true)
    }

    if (typeof window.IntersectionObserver !== 'function') {
      revealSection()
      return undefined
    }

    const observer = new window.IntersectionObserver(([entry]) => {
      setIsComparisonInView(entry.isIntersecting)
      if (entry.isIntersecting) setIsComparisonVisible(true)
    }, { threshold: 0.08, rootMargin: '0px 0px -5% 0px' })

    observer.observe(section)
    return () => observer.disconnect()
  }, [])

  const activateReactivationSignal = (signalId) => {
    setActiveReactivationSignal(signalId)
    setReactivationPulseKey((pulseKey) => pulseKey + 1)
  }

  const selectedReactivationSignal = reactivationSignals.find(
    ({ id }) => id === activeReactivationSignal,
  ) || reactivationSignals[0]

  return (
    <main className="about-page" id="about" tabIndex="-1">
      <section
        className={`about-page__hero${isMissionOpen ? ' about-page__hero--mission-open' : ''}`}
        aria-labelledby="about-title"
      >
        <div className="about-page__hero-copy">
          <div className="about-page__hero-eyebrow" aria-hidden="true">
            <span>Le manifeste</span>
            <i />
            <small>Manifesto</small>
          </div>

          <h1 id="about-title" data-about-hero-step="title">
            <span className="about-page__title-fr" lang="fr">
              Chaque langue oubliée
              <strong>est une mission.</strong>
            </span>{' '}
            <small className="about-page__title-en" lang="en">
              Every forgotten language
              <strong>is a mission.</strong>
            </small>
          </h1>

          <button
            type="button"
            className="about-page__mission"
            aria-expanded={isMissionOpen}
            aria-controls="about-mission-detail"
            data-about-hero-step="mission"
            data-about-mission-trigger=""
            onClick={() => setIsMissionOpen((isOpen) => !isOpen)}
          >
            <span className="about-page__mission-mark" aria-hidden="true" data-about-hero-motion="mission-spark">
              <FiTarget focusable="false" />
              <i />
              <i />
            </span>
            <span className="about-page__mission-copy">
              <span className="about-page__mission-kicker" aria-hidden="true">
                Notre raison d’être / Our purpose
              </span>
              <span id="about-mission-title-fr" className="about-page__mission-title about-page__mission-title--fr" lang="fr">
                Notre mission : réactiver ce qui dort en vous.
              </span>
              <span id="about-mission-title-en" className="about-page__mission-title about-page__mission-title--en" lang="en">
                Our mission: reactivate what’s already inside you.
              </span>
            </span>
            <span className="about-page__mission-toggle" aria-hidden="true">
              <FiChevronDown focusable="false" />
            </span>
          </button>

          <div
            id="about-mission-detail"
            className="about-page__hero-intro"
            role="region"
            aria-labelledby="about-mission-title-fr about-mission-title-en"
            data-about-hero-step="copy"
            data-about-mission-detail=""
            hidden={!isMissionOpen}
          >
            <p lang="fr">Nous croyons que chaque langue apprise peut être ravivée, à tout moment.</p>
            <small lang="en">We believe every language learned can be reactivated, anytime.</small>
          </div>
        </div>

        <div className="about-page__hero-art" aria-hidden="true" data-about-hero-motion="scene">
          <div className="about-page__hero-motes" />

          <svg
            className="about-page__language-currents"
            viewBox="0 0 1000 500"
            preserveAspectRatio="none"
            focusable="false"
            data-about-hero-motion="language-constellation"
          >
            <g className="about-page__current-network about-page__current-network--desktop">
              {heroLanguageCurrents.desktop.map((path, index) => (
                <path
                  className={`about-page__current${index > 2 ? ' about-page__current--secondary' : ''}`}
                  d={path}
                  pathLength="1"
                  style={{ '--about-current-delay': `${700 + index * 95}ms` }}
                  key={path}
                />
              ))}
              <path
                className="about-page__current about-page__current--mission"
                d="M625 145 C555 260 430 350 250 420"
                pathLength="1"
              />
              <path
                className="about-page__current-pulse"
                d="M625 145 C555 260 430 350 250 420"
                pathLength="1"
              />
              <circle className="about-page__language-hub-ring" cx="625" cy="145" r="22" />
              <circle className="about-page__language-hub" cx="625" cy="145" r="4" />
            </g>

            <g className="about-page__current-network about-page__current-network--mobile">
              {heroLanguageCurrents.mobile.map((path, index) => (
                <path
                  className={`about-page__current${index > 1 ? ' about-page__current--secondary' : ''}`}
                  d={path}
                  pathLength="1"
                  style={{ '--about-current-delay': `${700 + index * 95}ms` }}
                  key={path}
                />
              ))}
              <path
                className="about-page__current about-page__current--mission"
                d="M640 195 C545 285 420 350 260 425"
                pathLength="1"
              />
              <path
                className="about-page__current-pulse"
                d="M640 195 C545 285 420 350 260 425"
                pathLength="1"
              />
              <circle className="about-page__language-hub-ring" cx="640" cy="195" r="22" />
              <circle className="about-page__language-hub" cx="640" cy="195" r="4" />
            </g>
          </svg>

          <div className="about-page__language-orbit">
            <i />
            <i />
            <span />
          </div>

          <div className="about-page__greetings">
            {heroMemoryFragments.map(({ text, lang, code, className, dir }, index) => (
              <span
                className={`about-page__greeting about-page__greeting--${className}`}
                lang={lang}
                dir={dir}
                data-about-memory-fragment={code}
                style={{
                  '--about-greeting-delay': `${300 + index * 105}ms`,
                  '--about-greeting-drift-delay': `${index * -620}ms`,
                  '--about-greeting-duration': `${8 + index * 0.43}s`,
                  '--about-greeting-awaken-delay': `${120 + index * 78}ms`,
                }}
                key={className}
              >
                <small>{code}</small>
                <span>{text}</span>
              </span>
            ))}
          </div>

          <div className="about-page__hero-scroll">
            <span />
            <small>Découvrir le cycle</small>
          </div>
        </div>
      </section>

      <section
        ref={lifecycleSectionRef}
        className={`about-page__panel about-page__lifecycle${isLifecycleVisible ? ' about-page__lifecycle--visible' : ''}`}
        aria-labelledby="lifecycle-title"
      >
        <header className="about-page__lifecycle-header">
          <span className="about-page__lifecycle-eyebrow">
            <i aria-hidden="true" />
            <span lang="fr">Une mémoire en mouvement</span>
            <small lang="en">A memory in motion</small>
          </span>
          <h2 id="lifecycle-title">
            <span lang="fr">Le cycle d’une langue dans votre vie.</span>
            <small lang="en">The language lifecycle</small>
          </h2>
          <p className="about-page__lifecycle-thesis">
            <span lang="fr">Elle ne disparaît pas. Elle change d’état.</span>
            <small lang="en">It doesn’t disappear. It changes state.</small>
          </p>
        </header>

        <div className="about-page__lifecycle-stage">
          <div
            className="about-page__lifecycle-current"
            aria-hidden="true"
            data-lifecycle-motion="living-current"
          >
            <svg viewBox="0 0 1000 260" preserveAspectRatio="none" focusable="false">
              <defs>
                <linearGradient id="lifecycle-current-gradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stopColor="#f6a43a" />
                  <stop offset="0.5" stopColor="#759ab0" />
                  <stop offset="1" stopColor="#82ddf8" />
                </linearGradient>
              </defs>
              <path
                className="about-page__lifecycle-path about-page__lifecycle-path--base"
                d="M165 132 C285 132 352 72 500 72 C648 72 715 132 835 132"
                pathLength="1"
              />
              <path
                className="about-page__lifecycle-path about-page__lifecycle-path--draw"
                d="M165 132 C285 132 352 72 500 72 C648 72 715 132 835 132"
                pathLength="1"
              />
              <path
                className="about-page__lifecycle-path about-page__lifecycle-path--pulse"
                d="M165 132 C285 132 352 72 500 72 C648 72 715 132 835 132"
                pathLength="1"
              />
              <circle className="about-page__lifecycle-node about-page__lifecycle-node--learning" cx="165" cy="132" r="8" />
              <circle className="about-page__lifecycle-node about-page__lifecycle-node--forgetting" cx="500" cy="72" r="8" />
              <circle className="about-page__lifecycle-node about-page__lifecycle-node--reactivation" cx="835" cy="132" r="8" />
            </svg>
          </div>

          <ol className="about-page__lifecycle-journey">
            {lifecycleSteps.map((step, index) => (
              <li
                className={`about-page__lifecycle-chapter about-page__lifecycle-chapter--${step.tone}${visibleLifecycleSteps.includes(step.id) ? ' about-page__lifecycle-chapter--visible' : ''}`}
                data-lifecycle-chapter={step.id}
                style={{ '--lifecycle-step-delay': `${220 + index * 420}ms` }}
                key={step.id}
              >
                <article>
                  <div
                    className="about-page__lifecycle-visual"
                    aria-hidden="true"
                    data-lifecycle-motion={`${step.id}-scene`}
                  >
                    <span className="about-page__lifecycle-index">{step.number}</span>
                    <LifecycleNeuralMap state={step.id} />
                  </div>

                  <div className="about-page__lifecycle-copy">
                    <h3>
                      <span lang="fr">{step.titleFr}</span>
                      <small lang="en">{step.titleEn}</small>
                    </h3>
                    <p className="about-page__lifecycle-statement">
                      <span lang="fr">{step.statementFr}</span>
                      <small lang="en">{step.statementEn}</small>
                    </p>
                    <p lang="fr">{step.textFr}</p>
                    <small lang="en">{step.textEn}</small>
                  </div>
                </article>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section
        ref={reactivationSectionRef}
        className={`about-page__panel about-page__reactivation${isReactivationVisible ? ' about-page__reactivation--visible' : ''}${isReactivationInView ? ' about-page__reactivation--in-view' : ''}`}
        aria-labelledby="reactivation-title"
        data-reactivation-signal={activeReactivationSignal}
      >
        <div className="about-page__reactivation-ambient" aria-hidden="true" />

        <header className="about-page__reactivation-copy">
          <span className="about-page__reactivation-eyebrow">
            <i aria-hidden="true" />
            <span lang="fr">Le pouvoir du déjà-appris</span>
            <small lang="en">The power of what you already know</small>
          </span>
          <h2 id="reactivation-title">
            <span lang="fr">OctoSynapse : maître de la <strong>réactivation</strong></span>
            <small lang="en">OctoSynapse: designed to bring your language back</small>
          </h2>
          <p className="about-page__reactivation-lead" lang="fr">
            Vous reconnaissez le mot. Vous le retrouvez. <strong>Vous le dites.</strong>
          </p>
          <p className="about-page__reactivation-lead-en" lang="en">
            You recognize it. You recall it. <strong>You say it.</strong>
          </p>
          <p className="about-page__reactivation-body" lang="fr">
            OctoSynapse n’est pas un cours. Nous ne repartons pas de zéro : nous ravivons les connexions que votre mémoire possède déjà.
          </p>
          <p className="about-page__reactivation-body-en" lang="en">
            OctoSynapse is not another course. It reawakens the language pathways already inside you.
          </p>

          <div
            id="reactivation-signal-state"
            className="about-page__reactivation-state"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <span lang="fr">{selectedReactivationSignal.stateFr}</span>
            <small lang="en">{selectedReactivationSignal.stateEn}</small>
          </div>
        </header>

        <figure className="about-page__reactivation-stage">
          <ReactivationMemoryTree
            activeSignal={activeReactivationSignal}
            pulseKey={reactivationPulseKey}
          />
          <figcaption>
            <span lang="fr">Une langue en sommeil n’est pas une langue perdue.</span>
            <small lang="en">A dormant language is not a lost language.</small>
          </figcaption>
        </figure>

        <div
          className="about-page__reactivation-signals"
          role="group"
          aria-label="Signaux de réactivation / Reactivation signals"
        >
          {reactivationSignals.map((signal, index) => {
            const { id, number, icon: Icon, titleFr, titleEn, proofFr, proofEn } = signal
            const isActive = id === activeReactivationSignal

            return (
              <button
                type="button"
                className={`about-page__reactivation-signal${isActive ? ' about-page__reactivation-signal--active' : ''}`}
                aria-pressed={isActive}
                aria-controls="reactivation-signal-state"
                onClick={() => activateReactivationSignal(id)}
                onPointerEnter={() => activateReactivationSignal(id)}
                style={{ '--reactivation-signal-index': index }}
                key={id}
              >
                <span className="about-page__reactivation-signal-number" aria-hidden="true">
                  {number}
                </span>
                <span className="about-page__reactivation-signal-icon" aria-hidden="true">
                  <Icon focusable="false" />
                  <i />
                </span>
                <span className="about-page__reactivation-signal-copy">
                  <strong lang="fr">{titleFr}</strong>
                  <small lang="en">{titleEn}</small>
                  <span lang="fr">{proofFr}</span>
                  <em lang="en">{proofEn}</em>
                </span>
                <span className="about-page__reactivation-signal-line" aria-hidden="true" />
              </button>
            )
          })}
        </div>
      </section>

      <section
        ref={comparisonSectionRef}
        className={`about-page__panel about-page__comparison${isComparisonVisible ? ' about-page__comparison--visible' : ''}${isComparisonInView ? ' about-page__comparison--in-view' : ''}`}
        aria-labelledby="comparison-title"
      >
        <ComparisonNetwork />

        <header className="about-page__comparison-header">
          <p className="about-page__comparison-eyebrow">
            <i aria-hidden="true" />
            <span lang="fr">Une autre logique d’apprentissage</span>
            <small lang="en">A different learning logic</small>
          </p>
          <h2 id="comparison-title">
            <span lang="fr">Pourquoi <strong>OctoSynapse</strong> est unique</span>
            <small lang="en">Why <strong>OctoSynapse</strong> is unique</small>
          </h2>
          <p className="about-page__comparison-intro">
            <span lang="fr">Nous ne vous faisons pas recommencer. Nous remettons votre mémoire en mouvement.</span>
            <small lang="en">We do not make you start over. We put your memory back in motion.</small>
          </p>
        </header>

        <div
          className="about-page__comparison-table"
          role="table"
          aria-label="Comparaison entre les applications classiques et OctoSynapse / Comparison between traditional apps and OctoSynapse"
        >
          <div className="about-page__comparison-head" role="row">
            <div className="about-page__comparison-head-cell" role="columnheader">
              <span lang="fr">Dimension</span>
              <small lang="en">Dimension</small>
            </div>
            <div className="about-page__comparison-head-cell about-page__comparison-head-cell--classic" role="columnheader">
              <span className="about-page__comparison-head-status" aria-hidden="true">
                <FiMinus focusable="false" />
              </span>
              <span>
                <span lang="fr">Applications classiques</span>
                <small lang="en">Traditional apps</small>
              </span>
            </div>
            <span className="about-page__comparison-head-bridge" aria-hidden="true" />
            <div className="about-page__comparison-head-cell about-page__comparison-head-cell--octo" role="columnheader">
              <span className="about-page__comparison-live" aria-hidden="true">
                <i />
                Synapse active
              </span>
              <strong>OctoSynapse</strong>
            </div>
          </div>

          <div className="about-page__comparison-body" role="rowgroup">
            {comparisonRows.map(({
              icon: Icon,
              featureFr,
              featureEn,
              classicFr,
              classicEn,
              octoFr,
              octoEn,
            }, index) => (
              <div
                className="about-page__comparison-row"
                role="row"
                data-comparison-row={index + 1}
                style={{ '--comparison-row-index': index }}
                key={featureFr}
              >
                <div className="about-page__comparison-feature" role="rowheader">
                  <span className="about-page__comparison-index" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="about-page__comparison-icon" aria-hidden="true">
                    <Icon focusable="false" />
                    <i />
                  </span>
                  <span className="about-page__comparison-feature-copy">
                    <strong lang="fr">{featureFr}</strong>
                    <small lang="en">{featureEn}</small>
                  </span>
                </div>

                <div className="about-page__comparison-value about-page__comparison-value--classic" role="cell">
                  <span className="about-page__comparison-mobile-label">
                    <span lang="fr">Applications classiques</span>
                    <small lang="en">Traditional apps</small>
                  </span>
                  <span className="about-page__comparison-value-mark" aria-hidden="true">
                    <FiMinus focusable="false" />
                  </span>
                  <span className="about-page__comparison-value-copy">
                    <span lang="fr">{classicFr}</span>
                    <small lang="en">{classicEn}</small>
                  </span>
                </div>

                <span className="about-page__comparison-bridge" aria-hidden="true">
                  <i>
                    <b />
                  </i>
                  <FiArrowRight focusable="false" />
                </span>

                <div className="about-page__comparison-value about-page__comparison-value--octo" role="cell">
                  <span className="about-page__comparison-mobile-label">
                    <strong>OctoSynapse</strong>
                    <small>Memory-first</small>
                  </span>
                  <span className="about-page__comparison-value-mark" aria-hidden="true">
                    <FiCheck focusable="false" />
                    <i />
                  </span>
                  <span className="about-page__comparison-value-copy">
                    <strong lang="fr">{octoFr}</strong>
                    <small lang="en">{octoEn}</small>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <footer className="about-page__comparison-signature">
          <span className="about-page__comparison-signature-signal" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <p>
            <span lang="fr">Votre expérience n’est pas effacée. <strong>Elle est réactivée.</strong></span>
            <small lang="en">Your experience is not erased. <strong>It is reactivated.</strong></small>
          </p>
          <span className="about-page__comparison-count">
            <span lang="fr">différences fondamentales</span>
            <small className="about-page__comparison-count-label-en" lang="en">fundamental shifts</small>
          </span>
        </footer>
      </section>

      <FinalCtaPanel
        headingId="about-cta-title"
        titleFr="Votre langue n’a pas disparu."
        titleEn="Your language hasn’t disappeared."
        bodyFr="Elle attend simplement le bon contexte pour se réveiller."
        bodyEn="It is simply waiting for the right context to wake up."
        href={APP_ENTRY_HREF}
        label="Commencer ma réactivation"
        labelEn="Start my reactivation"
        activatingLabel="Réactivation en cours…"
        activatingLabelEn="Starting reactivation…"
      />
    </main>
  )
}

export default About
